import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { HttpErrorResponse } from "@angular/common/http";
import { Subject, Observable, firstValueFrom, of } from "rxjs";
import { DatePipe } from "@angular/common"
import { MatSidenav, MatDrawerContent } from "@angular/material/sidenav";
import { map, tap, catchError } from "rxjs/operators";
import { timer } from "rxjs";
import { DateTime } from "luxon";
import { environment } from "../../environments/environment";
import { Pair } from "../obj/Pair";
import { RateData } from "../obj/RateData";
import { ChartData } from "../obj/ChartData";
import { AccountData } from "../obj/AccountData";
import { AccountConsumption } from "../obj/AccountConsumption";
import { OffersData } from "../obj/OffersData";
import { CurrentContractData } from "../obj/CurrentContractData";
import { EntityBase } from "../entity/EntityBase";
import { User } from "../entity/User";
import { Company } from "../entity/Company";
import { Utility } from "../entity/Utility";
import { FuelType } from "../entity/FuelType";
import { State } from "../entity/State";
import { Account } from "../entity/Account";
import { BuyOffer } from "../entity/BuyOffer";
import { Bid } from "../entity/Bid";
import { SellOffer } from "../entity/SellOffer";
import { SellerContract } from "../entity/SellerContract";
import { Contract } from "../entity/Contract";
import { ExistingContract } from "../entity/ExistingContract";
import { KilowantContract } from "../entity/KilowantContract";
import { AuthService } from "./auth.service";
import { RateClass } from "../entity/RateClass";
import { LoadZone } from "../entity/LoadZone";
import { KilowantFee } from "../entity/KilowantFee";
import { AlertSubscription } from "../entity/AlertSubscription";
import { BatchExport } from "../entity/BatchExport";
import { AITask } from "../entity/AITask";
import { UtilityCommunityData } from "../obj/UtilityCommunityData";
import { Stats } from "../obj/Stats";
import { AccountSelector } from "../obj/AccountSelector";
import { FAQ } from "../entity/FAQ";
import { OffersResults } from "../obj/OffersResults";
import { AccountSelection } from "../obj/AccountSelection";
import { ConsumptionDataType } from "../obj/ConsumptionDataType";
import { ClientFilter } from "../obj/ClientFilter";
import { TotalSavingsInfo } from "../obj/TotalSavingsInfo";
import { PackageData } from "../obj/PackageData";
import { CacheService } from "./cache.service";

@Injectable({
    providedIn: "root"
})
export class GeneralService {
    public BETA_UTILITY_ID: number = 14;
    public BETA_LOAD_ZONE_ID: number = 7;
    public supportedStateCode = "RI";
    public MINIMAL_GAP_PERIOD = 1;

    private _TIMESTAMPTRESHOLD = 40000;

    _siteConfig: Pair[];
    _siteConfigRequest: Promise<Pair[]>;
    _siteUserRequest: Promise<User> | null;
    _sidenav: MatSidenav = null!;

    _pageContent: MatDrawerContent;
    _headerHeight: number;

    _topOffers: number[] = [];

    private changeHighlightedSource = new Subject<boolean>();
    changeHighlightedActivities$ = this.changeHighlightedSource.asObservable();

    private changeAccountSource = new Subject<Account | null>();
    changeAccountActivities$ = this.changeAccountSource.asObservable();

    private newContractCreatedSource = new Subject<number>();
    newContractCreated$ = this.newContractCreatedSource.asObservable();

    private changeClientSource = new Subject<User | null>();
    changeClientActivities$ = this.changeClientSource.asObservable();

    private changeUtilitySource = new Subject<Utility | null>();
    changeUtilityActivities$ = this.changeUtilitySource.asObservable();

    private changeRateClassSource = new Subject<RateClass | null>();
    changeRateClassActivities$ = this.changeRateClassSource.asObservable();

    private changeIsPreRegisterSource = new Subject<User>();
    changeIsPreRegisterSource$ = this.changeIsPreRegisterSource.asObservable();


    private _lastTs: number = 0;
    private _requestTimeStamp: Promise<number> | null;
    private _lastNow: number = 0;

    private _totalOffers: number = 0;


    private http = inject(HttpClient);
    private datepipe = inject(DatePipe);
    private authService = inject(AuthService);
    private cacheService = inject(CacheService);

    constructor() {
        this._siteConfig = null!;
        this._siteConfigRequest = null!;
        this._siteUserRequest = null!;
    }

    send(data: any): Observable<any> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        let httpBody = new HttpParams();
        for (var key in data) {
            if (Array.isArray(data[key])) {
                httpBody = httpBody.set(key, data[key].join(", "));
            } else {
                httpBody = httpBody.set(key, data[key]);
            }
        }
        return this.http
            .post(
                environment.WEB_API_URL + "Send",
                httpBody,
                { headers: headers }
            );
    }

    getSiteConfig(): Promise<Pair[]> {
        if (this._siteConfig != null) {
            return Promise.resolve(this._siteConfig);
        }
        if (this._siteConfigRequest != null) {
            return this._siteConfigRequest;
        }
        this._siteConfigRequest = <Promise<Pair[]>>this.http.get(environment.WEB_API_URL + "SiteConfig")
            .toPromise()
            .then((res: any) => {
                this._siteConfig = <Pair[]>res;
                return this._siteConfig;
            });
        return this._siteConfigRequest;
    }

    now(): Promise<number> {
        let d = Date.now();
        if (d - this._lastTs <= this._TIMESTAMPTRESHOLD) {
            return Promise.resolve(this._lastNow);
        }
        if (this._requestTimeStamp != null) {
            return this._requestTimeStamp;
        }
        this._requestTimeStamp = <Promise<number>>this.http.get(environment.WEB_API_URL + "Now")
            .toPromise()
            .then((res: any) => {
                this._lastTs = Date.now();
                this._lastNow = <number>res;
                this._requestTimeStamp = null;
                return this._lastNow;
            });
        return this._requestTimeStamp;
    }

    totalOffers(): Observable<number> {
        if (this._totalOffers > 0) {
            return of(this._totalOffers);
        }
        return this.http.get(environment.WEB_API_URL + "TotalOffers")
            .pipe(
                map(response => {
                    this._totalOffers = <number>response;
                    return <number>response;
                })
            );
    }

    IsBusinessTime(): Promise<boolean> {
        return <Promise<boolean>>this.http.get(environment.WEB_API_URL + "IsBusinessTime")
            .toPromise()
            .then((res: any) => {
                return <boolean>res;
            });
    }

    getFuelTypes(): Promise<FuelType[]> {
        return <Promise<FuelType[]>>this.http.get(environment.WEB_API_URL + "FuelType/List")
            .toPromise()
            .then((res: any) => {
                return <FuelType[]>res;
            });
    }

    getStates(all: boolean): Promise<State[]> {
        return <Promise<State[]>>this.http.get(environment.WEB_API_URL + "State/List")
            .toPromise()
            .then((res: any) => {
                let data: State[] = [];
                for (let i = 0; i < (<State[]>res).length; i++) {
                    if (all || (<State[]>res)[i].stateCode == this.supportedStateCode) {
                        data.push((<State[]>res)[i]);
                    }
                }
                return data;
            });
    }

    getCities(state: State): Promise<string[]> {
        let queryParams = new HttpParams();
        if (state != null) {
            queryParams = queryParams.set("stateId", state.id);
        }
        return <Promise<string[]>>this.http.get(environment.WEB_API_URL + "Zip/City/List", { params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <string[]>res;
            });
    }

    getDefaultFuelType(): FuelType {
        var res = new FuelType();
        res.id = 1;
        res.name = "Electric";
        res.isTrackable = true;
        res.isDefault = true;
        res.usesDelivery = true;
        res.facilityBased = true;
        res.trackContracts = true;
        res.isMetered = true;
        res.dividesDay = true;
        res.isActive = true;
        res.carbonFootprintCoof = 0;
        res.MBTU2CoofUnits = 0;
        res.MMBTU2CoofUnitCost = 0;
        res.unitsOfMeasure = [];
        return res;
    }

    storeInSession(name: string, obj: Object): void {
        sessionStorage.setItem("session_stored_" + name, JSON.stringify(obj));
    }
    getFromSession(name: string): Object | null {
        let data = sessionStorage.getItem("session_stored_" + name);
        if (data == null) {
            return null;
        }
        return JSON.parse(data);
    }
    clearFromSessionByPrefix(prefix: string): void {
        let sessionKeys = Object.keys(sessionStorage);
        for (let i = 0; i < sessionKeys.length; i++) {
            if (sessionKeys[i].startsWith("session_stored_" + prefix)) {
                sessionStorage.removeItem(sessionKeys[i]);
            }
        }
    }

    storeInStorage(name: string, obj: Object): void {
        localStorage.setItem("starage_stored_" + name, JSON.stringify(obj));
    }
    getFromStorage(name: string): Object | null {
        let data = localStorage.getItem("starage_stored_" + name);
        if (data == null) {
            return null;
        }
        return JSON.parse(data);
    }

    getStateCityByZip(code: string): Observable<Pair | null> | null {
        if (code.length < 3 || code.length > 5) {
            return null;
        }
        return this.http.get<any>(environment.WEB_API_URL + "Zip/" + code);
    }

    getStoredAccount(): Account | null {
        var accountData = sessionStorage.getItem("selected_account");
        if (accountData == null) {
            return null;
        }
        var account = <Account>JSON.parse(accountData);
        if (account.id == 0 || account.id == null) {
            this.clearStoredAccount();
            return null;
        }
        return account;
    }

    saveAffiliate(name: string): void {
        this.storeInStorage("affiliate", name);
    }

    switchStoredAccount(account: Account): void {
        sessionStorage.setItem("selected_account", JSON.stringify(account));
        this.changeAccountSource.next(account);
    }

    setTopOfferIds(offers: OffersData[]): void {
        this._topOffers = [];
        for (let i = 0; i < offers.length; i++) {
            this._topOffers.push(offers[i].offer.id);
        }
    }

    getTopOfferIds(): number[] {
        return this._topOffers;
    }

    topOfferPosition(offerId: number): number {
        for (let i = 0; i < this._topOffers.length; i++) {
            if (this._topOffers[i] == offerId) {
                return i + 1;
            }
        }
        return 0;
    }

    getStoredClient(): User | null {
        var clientData = sessionStorage.getItem("selected_client");
        if (clientData == null) {
            return null;
        }
        var client = <User>JSON.parse(clientData);
        if (client.id == 0 || client.id == null) {
            this.clearStoredClient();
            return null;
        }
        return Object.assign(new User(), client);
    }

    switchStoredClient(client: User, clientFilter: ClientFilter | null): void {
        sessionStorage.setItem("selected_client", JSON.stringify(client));
        this.clearUserCompaniesFromSession();
        if (clientFilter != null) {
            if (clientFilter.hasMultipleCompanies) {
                if (clientFilter.isResidential) {
                    this.switchStoredUserFilter(false, null);
                } else {
                    var company = new Company();
                    company.id = clientFilter.companyId;
                    company.name = clientFilter.clientName;
                    this.switchStoredUserFilter(true, company);
                }
            } else {
                this.clearStoredUserFilter();
            }
        }
        this.changeClientSource.next(client);
    }

    changeIsPreRegister(u: User): void {
        this.changeIsPreRegisterSource.next(u);
    }

    getStoredUserFilter(): Pair | null {
        var type = sessionStorage.getItem("selected_user_filter");
        if (type == null) {
            return null;
        }
        if ("residential" == type) {
            return new Pair(false, null);
        }
        var companyData = sessionStorage.getItem("selected_user_company");
        if (companyData == null) {
            return null;
        }
        var company = <Company>JSON.parse(companyData);
        if (company.id == 0 || company.id == null) {
            this.clearStoredClient();
            return null;
        }
        return new Pair(true, company);
    }

    switchStoredUserFilter(isCommerical: boolean, company: Company | null): void {
        if (!isCommerical) {
            sessionStorage.removeItem("selected_user_company");
            sessionStorage.setItem("selected_user_filter", "residential");
            return;
        }
        sessionStorage.setItem("selected_user_filter", "commercial");
        sessionStorage.setItem("selected_user_company", JSON.stringify(company));
    }

    clearStoredUserFilter(): void {
        sessionStorage.removeItem("selected_user_filter");
        sessionStorage.removeItem("selected_user_company");
    }

    switchStoredUtility(utility: Utility): void {
        this.changeUtilitySource.next(utility);
    }

    switchStoredRateClass(rateClass: RateClass): void {
        this.changeRateClassSource.next(rateClass);
    }

    clearStoredAccount(): void {
        sessionStorage.removeItem("selected_account");
        this.clearFromSessionByPrefix("account_data_");
        this.changeAccountSource.next(null);
    }

    clearStoredClient(): void {
        sessionStorage.removeItem("selected_client");
        this.changeClientSource.next(null);
    }

    storeSuggestedOffer(suggestedPrice: number, suggestedStartDate: DateTime, suggestedEndDate: DateTime): void {
        sessionStorage.setItem("suggestedPrice", JSON.stringify(suggestedPrice));
        sessionStorage.setItem("suggestedStartDate", this.stringOrNull(suggestedStartDate.toISO()));
        sessionStorage.setItem("suggestedEndDate", this.stringOrNull(suggestedEndDate.toISO()));
    }

    getStoredSuggestedPrice(): number | null {
        var data = sessionStorage.getItem("suggestedPrice");
        if (data == null) {
            return null;
        }
        return <number>JSON.parse(data);
    }

    getStoredSuggestedStartDate(): DateTime | null {
        var data = sessionStorage.getItem("suggestedStartDate");
        if (data == null) {
            return null;
        }
        return DateTime.fromISO(data);
    }

    getStoredSuggestedEndDate(): DateTime | null {
        var data = sessionStorage.getItem("suggestedEndDate");
        if (data == null) {
            return null;
        }
        return DateTime.fromISO(data);
    }

    getFAQ(limit: number | null): Observable<FAQ[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        queryParams = queryParams.set("limit", (limit == null) ? 0 : limit);

        return this.http
            .get<any>(
                environment.WEB_API_URL + "FAQ",
                { headers: headers, params: queryParams }
            );
    }
    getUtilities(fuelType: FuelType | null, activeOnly: boolean = true, hierarchy: boolean = false, state: State | null = null): Promise<Utility[]> {
        return this.getUtilitiesPlusKeywords(fuelType, activeOnly, null, hierarchy, state);
    }

    getUtilitiesPlusKeywords(fuelType: FuelType | null, activeOnly: boolean = true, keywords: string | null, hierarchy: boolean = false, state: State | null = null): Promise<Utility[]> {
        var url = environment.WEB_API_URL + "Utilities";

        let queryParams = new HttpParams();
        if (fuelType != null) {
            queryParams = queryParams.set("id", fuelType.id);
        }
        if (state != null) {
            queryParams = queryParams.set("stateId", state.id);
        }
        if (keywords != null) {
            queryParams = queryParams.set("keywords", keywords);
        }
        queryParams = queryParams.set("activeOnly", activeOnly);
        queryParams = queryParams.set("hierarchy", hierarchy);
        return <Promise<Utility[]>>this.http.get(url, { params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Utility[]>res;
            });
    }
    getUtility(id: number, activeOnly: boolean = true): Promise<Utility> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        queryParams = queryParams.set("activeOnly", activeOnly);

        return <Promise<Utility>>this.http
            .get<any>(
                environment.WEB_API_URL + "Utility/" + id,
                { headers: headers, params: queryParams }
            )
            .toPromise()
            .then((res: any) => {
                return <Utility>res;
            });
    }
    getDefaultUtilityFromList(utilities: Utility[]): Utility | null {
        for (let i = 0; i < utilities.length; i++) {
            if (utilities[i].isDefault) {
                return utilities[i];
            }
        }
        if (utilities.length > 0) {
            return utilities[0];
        }
        return null;
    }

    getRateClasses(utility: Utility): Promise<RateClass[]> {
        if (utility.rateClasses != null && utility.rateClasses.length > 0) {
            return Promise.resolve(utility.rateClasses);
        }
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<RateClass[]>>this.http
            .get<any>(
                environment.WEB_API_URL + "Utility/" + utility.id,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <RateClass[]>res.rateClasses;
            });
    }

    getRateClassesPerMultipleUtilities(utilities: Utility[]): Promise<RateClass[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        const utilityIdsJSON = JSON.stringify(EntityBase.convertListToIDsArray(utilities));
        return <Promise<RateClass[]>>this.http
            .get<any>(
                environment.WEB_API_URL + "RateClass/List?utilities=" + utilityIdsJSON,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <RateClass[]>res;
            });
    }

    getLoadZones(utility: Utility): Promise<LoadZone[]> {
        if (utility.rateClasses != null && utility.rateClasses.length > 0) {
            return Promise.resolve(utility.loadZones);
        }
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<LoadZone[]>>this.http
            .get<any>(
                environment.WEB_API_URL + "Utility/" + utility.id,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <LoadZone[]>res.loadZones;
            });
    }

    getLoadZonesPerMultipleUtilities(utilities: Utility[]): Promise<LoadZone[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        const utilityIdsJSON = JSON.stringify(EntityBase.convertListToIDsArray(utilities));
        return <Promise<LoadZone[]>>this.http
            .get<any>(
                environment.WEB_API_URL + "LoadZone/List?utilities=" + utilityIdsJSON,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <LoadZone[]>res;
            });
    }

    getRates(startDate: Date | null, endDate: Date | null, termPeriod: number | null, accounts: Account[]): Promise<RateData[]> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        var accountIds = [];
        for (var i = 0; i < accounts.length; i++) {
            accountIds[accountIds.length] = accounts[i].id;
        }
        if (startDate != null) {
            formData.append("startDate", this.datepipe.transform(startDate, "MM/dd/yyyy") ?? "");
        }
        if (endDate != null) {
            formData.append("endDate", this.datepipe.transform(endDate, "MM/dd/yyyy") ?? "");
        }
        if (termPeriod != null) {
            formData.append("termPeriod", termPeriod + "");
        }

        formData.append("accountIds", JSON.stringify(accountIds));

        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<RateData[]>>this.http
            .post<any>(
                environment.WEB_API_URL + "Chart/Rates",
                formData,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <RateData[]>res;
            });
    }

    getRatesForHomePage(type: string): Observable<RateData[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        queryParams = queryParams.set("type", type);
        return this.http.get(environment.WEB_API_URL + "Home/Chart/Rates", { headers: headers, params: queryParams }).pipe(
            map((res: any) => {
                return <RateData[]>res;
            })
        );
    }

    getLatestRate(accounts: Account[]): Promise<number> {
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        var accountIds = [];

        for (var i = 0; i < accounts.length; i++) {
            accountIds[accountIds.length] = accounts[i].id;
        }
        formData.append("accountIds", JSON.stringify(accountIds));

        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<number>>this.http
            .post<any>(
                environment.WEB_API_URL + "Chart/Rates/Latest",
                formData,
                { headers: headers }
            )
            .toPromise()
            .then((res: any) => {
                return <number>res;
            });
    }

    getAverageForRates(rates: RateData[]): number {
        if (rates == null || rates.length == 0) {
            return 0;
        }
        let s = 0;
        let c = 0;
        for (let i = 0; i < rates.length; i++) {
            if (rates[i].series != null && rates[i].series.length > 0 && rates[i].series[0].value > 0) {
                s += rates[i].series[0].value;
                c++;
            }
        }
        if (c > 0) {
            return s / c;
        }
        return 0;
    }

    getExistingContracts(account: Account | null): Promise<ExistingContract[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (account != null) {
            queryParams = queryParams.set("accountId", account.id);
        }
        return <Promise<ExistingContract[]>>this.http.get(environment.WEB_API_URL + "ExistingContract/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <ExistingContract[]>res;
            });
    }

    getExistingContract(id: number): Promise<ExistingContract> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<ExistingContract>>this.http.get(environment.WEB_API_URL + "ExistingContract/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return Object.assign(new ExistingContract(), res);
            });
    }

    getContracts(account: Account | null, hasFilter: boolean, filterCompany: Company | null, excludeInactive: boolean): Promise<Contract[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (account != null) {
            queryParams = queryParams.set("accountId", account.id);
        }
        if (hasFilter) {
            queryParams = queryParams.set("hasFilter", "true");
            if (filterCompany != null) {
                queryParams = queryParams.set("filterCompany", filterCompany.id);
            }
        }
        if (excludeInactive) {
            queryParams = queryParams.set("excludeInactive", true);
        }

        return <Promise<Contract[]>>this.http.get(environment.WEB_API_URL + "Contract/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                var res = [];
                for (var i = 0; i < data.length; i++) {
                    if (data[i]["$type"].includes("ExistingContract")) {
                        res.push(Object.assign(new ExistingContract(), data[i]));
                    }
                    if (data[i]["$type"].includes("KilowantContract")) {
                        res.push(Object.assign(new KilowantContract(), data[i]));
                    }
                }
                return res;
            });
    }

    getContractsForSeller(total: number, page: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        let queryParams = new HttpParams();
        queryParams = queryParams.set("total", total);
        queryParams = queryParams.set("page", page + 1);
        queryParams = queryParams.set("sortBy", sortBy);
        queryParams = queryParams.set("isDesc", isDesc);

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Seller/Contract/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var dataPair = <Pair>res;
                var kilowantContracts = [];
                for (var i = 0; i < dataPair.value.length; i++) {
                    kilowantContracts.push(Object.assign(new KilowantContract(), dataPair.value[i]));
                }
                this.changeHighlightedSource.next(true);
                return new Pair(dataPair.key, kilowantContracts);
            });
    }

    getContract(id: number): Promise<KilowantContract> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<KilowantContract>>this.http.get(environment.WEB_API_URL + "Contract/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return Object.assign(new KilowantContract(), res);
            });
    }

    getContractAsPDF(id: number): Observable<any> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http
            .get(
                environment.WEB_API_URL + "Contract/Export/" + id,
                {
                    headers: headers,
                    params: queryParams,
                    responseType: "arraybuffer"
                }
            );
    }

    getContractSavings(id: number): Promise<CurrentContractData> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<CurrentContractData>>this.http.get(environment.WEB_API_URL + "Contract/Savings/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <CurrentContractData>res;
            });
    }
    getExistingContractSavings(id: number): Promise<CurrentContractData> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<CurrentContractData>>this.http.get(environment.WEB_API_URL + "ExistingContract/Savings/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <CurrentContractData>res;
            });
    }

    getContractPendingCancelDate(contract: KilowantContract): Promise<Date | null> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Date | null>>this.http.get(environment.WEB_API_URL + "Contract/Pending/CancelUntil/" + contract.id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Date | null>res;
            });
    }

    getHighlights(): Promise<{ [key: string]: number }> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        return <Promise<{ [key: string]: number }>>this.http.get(environment.WEB_API_URL + "Seller/Highlights", { headers: headers })
            .toPromise()
            .then((res: any) => {
                return <{ [key: string]: number }>res;
            });
    }

    getAITasks(): Observable<AITask[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http.get(environment.WEB_API_URL + "Buyer/AITask/List",
            { headers: headers, params: queryParams })
            .pipe(map(response => {
                return <AITask[]>response;
            }
            )
            );
    }

    getAITask(id: number): Promise<AITask> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<AITask>>this.http.get(environment.WEB_API_URL + "AITask/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                let aiTask = <AITask>res;
                return aiTask;
            });
    }
    getAITaskPerContract(contractId: number): Promise<AITask | null> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<AITask>>this.http.get(environment.WEB_API_URL + "Contract/AITask/" + contractId, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                if (res == null) {
                    return null;
                }
                let aiTask = <AITask>res;
                return aiTask;
            });
    }

    switchAITaskLevel(aIds: number[], level: number): Observable<any> {
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        const accountsIds = JSON.stringify(aIds);
        formData.append("accountIds", accountsIds);
        formData.append("level", "" + level);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/AITask/SwitchLevel",
                formData,
                { headers: headers }
            );
    }

    addAITasks(aIds: number[], level: number, user: User | null, password: string | null): Observable<any> {
        const accountsIds = JSON.stringify(aIds);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("accounts", accountsIds);
        formData.append("level", "" + level);
        if (user != null) {
            formData.append("user", JSON.stringify(user));
        }
        if (password != null) {
            formData.append("password", password);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/AITasks/Add",
                formData,
                { headers: headers }
            );
    }

    deleteAITask(id: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("id", "" + id);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/AITask/Delete",
                formData,
                { headers: headers }
            );
    }

    cancelContract(id: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.set("id", "" + id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Contract/Cancel",
                formData,
                { headers: headers }
            );
    }

    cancelPendingContractBySeller(contract: KilowantContract, declineReasons: string): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("declineReasons", declineReasons);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/Contract/Cancel/" + contract.id,
                formData,
                { headers: headers }
            );
    }

    checkRegisterUserEmail(email: string): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("email", email);
        let headers = new HttpHeaders()
            .set("Accept", "application/json");
        let url = environment.WEB_API_URL + "User/CheckEmail";
        return this.http
            .post<any>(
                url,
                formData,
                { headers: headers }
            );

    }

    checkUpdateUserEmail(email: string): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("email", email);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        let url = environment.WEB_API_URL + "User/UpdateCheckEmail";
        return this.http
            .post<any>(
                url,
                formData,
                { headers: headers }
            );

    }

    registerUser(user: User, linkContactId: number, account: Account | null, password: string | null, taxExemptCertificateDocument: File | null, byAdvisor: boolean): Observable<any> {
        const userJson = JSON.stringify(user);
        const formData: FormData = new FormData();
        formData.append("user", userJson);
        if (account != null) {
            const accountJson = JSON.stringify(account);
            formData.append("account", accountJson);
        }
        if (linkContactId != null && linkContactId > 0) {
            formData.append("linkContactId", "" + linkContactId);
        }
        if (password != null) {
            formData.append("password", password);
        }
        if (taxExemptCertificateDocument != null) {
            formData.append("taxExemptCertificateDocument", taxExemptCertificateDocument, taxExemptCertificateDocument.name);
        }
        let affiliate = this.getFromStorage("affiliate");
        if (affiliate != null) {
            formData.append("affiliate", <string>affiliate);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json");
        let url = environment.WEB_API_URL + "User/Register";
        if (byAdvisor) {
            headers = headers.set("Authorization", this.authService.getBearer());
            url = environment.WEB_API_URL + "Advisor/User/Add";
        }
        return this.http
            .post<any>(
                url,
                formData,
                { headers: headers }
            );
    }

    updateUser(user: User, linkContactId: number, taxExemptCertificateDocument: File | null, deleteTaxExemptCertificateDocument: boolean, byAdvisor: boolean): Observable<any> {
        const userJson = JSON.stringify(user);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());

        const formData: FormData = new FormData();
        formData.append("user", userJson);

        if (linkContactId != null && linkContactId > 0) {
            formData.append("linkContactId", "" + linkContactId);
        }

        if (taxExemptCertificateDocument != null) {
            formData.append("taxExemptCertificateDocument", taxExemptCertificateDocument, taxExemptCertificateDocument.name);
        }

        if (deleteTaxExemptCertificateDocument) {
            formData.append("deleteTaxExemptCertificateDocument", "true");
        }

        let url = environment.WEB_API_URL + "User/Update";
        if (byAdvisor) {
            url = environment.WEB_API_URL + "Advisor/User/Update";
        }

        return this.http
            .post<any>(
                url,
                formData,
                { headers: headers }
            );
    }

    deleteClient(id: number): Observable<any> {
        var httpBody = new HttpParams()
            .set("id", id);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Advisor/User/Delete",
                httpBody,
                { headers: headers }
            );
    }

    setClientSelfManaged(id: number): Observable<any> {
        var httpBody = new HttpParams()
            .set("id", id);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Advisor/User/SetSelfManaged",
                httpBody,
                { headers: headers }
            );
    }

    addExistingCompany(id: string) {
        var httpBody = new HttpParams()
            .set("companyId", id);
        let headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8").set("Authorization", this.authService.getBearer());
        return this.http.post<any>(
            environment.WEB_API_URL + "Seller/ApplyToExistingCompany", httpBody, { headers: headers });
    }

    clearUserCache(): void {
        this._siteUserRequest = null;
        this.clearFromSessionByPrefix("user_details");
    }

    updateUtilities(user: User): Observable<any> {
        var data: number[] = [];
        for (var i = 0; i < user.utilities.length; i++) {
            data.push(user.utilities[i].id);
        }
        const utilitiesJson = JSON.stringify(data);
        var httpBody = new HttpParams()
            .set("utilities", utilitiesJson);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/UpdateUtilities",
                httpBody,
                { headers: headers }
            )
            .pipe(tap(() => this.clearUserCache()));
    }

    updateAccount(account: Account, filesToUpload: File[], filesToDelete: number[], taxExemptCertificateDocument: File | null): Observable<any> {
        const accountJson = JSON.stringify(account);
        const filesToDeleteJson = JSON.stringify(filesToDelete);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        for (var i = 0; i < filesToUpload?.length; i++) {
            formData.append("billImages", filesToUpload[i], filesToUpload[i].name);
        }
        if (taxExemptCertificateDocument != null) {
            formData.append("taxExemptCertificateDocument", taxExemptCertificateDocument, taxExemptCertificateDocument.name);
        }
        formData.append("account", accountJson);
        formData.append("filesToDelete", filesToDeleteJson);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/Update",
                formData,
                { headers: headers }
            );
    }

    ifAccountNumberUsed(utility: Utility, accountNumber: string, updateAccountId: number | null): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("utilityId", "" + utility.id);
        if (updateAccountId != null && updateAccountId > 0) {
            queryParams = queryParams.set("updateAccountId", updateAccountId);
        }
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Account/CheckAccountNumber/" + accountNumber, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Pair>res;
            });

    }

    reportAccountNumberUsed(utility: Utility | null, accountNumber: string | null, message: string | null): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (utility != null) {
            queryParams = queryParams.set("utilityId", "" + utility!.id);
        }
        queryParams = queryParams.set("message", (message == null) ? "" : message);
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Account/ReportAccountNumberIssue/" + accountNumber, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Pair>res;
            });

    }

    getUtilityCosts(utility: Utility, rateClass: RateClass, loadZone: LoadZone, state: State, city: string): Promise<UtilityCommunityData> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("utilityId", utility.id);
        queryParams = queryParams.set("loadZoneId", loadZone.id);
        queryParams = queryParams.set("rateClassId", rateClass.id);
        queryParams = queryParams.set("stateId", state.id);
        queryParams = queryParams.set("city", city);

        return <Promise<UtilityCommunityData>>this.http.get(environment.WEB_API_URL + "Account/Add/UtilityCosts", { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                return data;
            });
    }

    addAccount(account: Account, existingContractId: number | null, existingContract: ExistingContract | null, consumptions: AccountConsumption[], filesToUpload: File[], taxExemptCertificateDocument: File | null): Observable<number> {
        let accountJson = JSON.stringify(account);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        for (var i = 0; i < filesToUpload!.length; i++) {
            formData.append("billImages", filesToUpload[i], filesToUpload[i].name);
        }
        if (taxExemptCertificateDocument != null) {
            formData.append("taxExemptCertificateDocument", taxExemptCertificateDocument, taxExemptCertificateDocument.name);
        }
        formData.append("account", accountJson);
        if (existingContractId != null && existingContractId > 0) {
            formData.append("existingContractId", "" + existingContractId);
        }
        if (existingContract != null) {
            let existingContractJson = JSON.stringify(existingContract);
            formData.append("existingContract", existingContractJson);
        }
        if (consumptions != null && consumptions.length > 0) {
            let consumptionJSON = JSON.stringify(consumptions);
            formData.append("consumption", consumptionJSON);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        var a = account;
        return this.http
            .post<number>(
                environment.WEB_API_URL + "Account/Add",
                formData,
                { headers: headers, reportProgress: false }
            ).pipe(map(response => {
                if ((<number>response) > 0) {
                    a.id = ((<number>response));
                }
                return response;
            }
            )
            );
    }

    addBillToAccount(account: Account, billToUpload: File): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);

        formData.append("accountId", String(account.id));
        formData.append("billImages", billToUpload, billToUpload.name);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/AddBill",
                formData,
                { headers: headers }
            );
    }

    removeBillFromAccount(account: Account, documentId: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);

        formData.append("accountId", String(account.id));
        formData.append("documentId", String(documentId));
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/RemoveBill",
                formData,
                { headers: headers }
            );
    }

    parseBill(billToUpload: File[]): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);

        for (var i = 0; i < billToUpload!.length; i++) {
            formData.append("billImages", billToUpload[i], billToUpload[i].name);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/ParseBill",
                formData,
                { headers: headers }
            );
    }

    parseBillAndRegister(billToUpload: File[]): Observable<any> {
        let formData: FormData = new FormData();

        for (var i = 0; i < billToUpload!.length; i++) {
            formData.append("billImages", billToUpload[i], billToUpload[i].name);
        }
        let affiliate = this.getFromStorage("affiliate");
        if (affiliate != null) {
            formData.append("affiliate", <string>affiliate);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/PreRegister",
                formData,
                { headers: headers }
            ).pipe(map(response => {
                this.clearFromSessionByPrefix("preRegisterData");
                this.storeInSession("preRegisterData", response);
                return response;
            }
            )
            );
    }

    parseBillAndRegisterContinue(userData: string, account: Account, existingContract: ExistingContract | null, consumption: AccountConsumption[] | null, taxExemptCertificateDocument: File | null): Observable<any> {
        let accountJson = JSON.stringify(account);
        let formData: FormData = new FormData();

        formData.append("user", userData);

        if (taxExemptCertificateDocument != null) {
            formData.append("taxExemptCertificateDocument", taxExemptCertificateDocument, taxExemptCertificateDocument.name);
        }
        formData.append("account", accountJson);

        if (existingContract != null) {
            let existingContractJson = JSON.stringify(existingContract);
            formData.append("existingContract", existingContractJson);
        }
        if (consumption != null && consumption.length > 0) {
            let consumptionJSON = JSON.stringify(consumption);
            formData.append("consumption", consumptionJSON);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/PreRegisterContinue",
                formData,
                { headers: headers }
            ).pipe(map(response => {
                this.authService.authenticateBaseOnResponse(response);
                return response;
            }
            )
            );
    }

    finishPreRegister(user: User, password: string): Observable<any> {
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        let formData: FormData = new FormData();
        formData.append("user", JSON.stringify(user));
        formData.append("password", password);
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/PreRegisterFinish",
                formData,
                { headers: headers }
            );
    }

    addExistingContract(existingContract: ExistingContract, fileToUpload: File | null): Observable<any> {
        let existingContractJSON = JSON.stringify(existingContract);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("existingContract", existingContractJSON);
        if (fileToUpload != null) {
            formData.append("contractDocument", fileToUpload, fileToUpload.name);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "ExistingContract/Add",
                formData,
                { headers: headers }
            );
    }

    updateExistingContract(existingContract: ExistingContract, fileToUpload: File | null, deleteContractDocument: boolean): Observable<any> {
        let existingContractJSON = JSON.stringify(existingContract);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("existingContract", existingContractJSON);
        if (fileToUpload != null) {
            formData.append("contractDocument", fileToUpload, fileToUpload.name);
        }
        if (deleteContractDocument) {
            formData.append("deleteContractDocument", "true");
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "ExistingContract/Update",
                formData,
                { headers: headers }
            );
    }

    updateUserPassword(oldPassword: string, password: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("oldPassword", oldPassword)
            .set("password", password);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/UpdatePassword",
                httpBody,
                { headers: headers }
            );
    }

    activateUser(data: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("data", data);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/Activation",
                httpBody,
                { headers: headers }
            );
    }

    changeUserOnboarding(user: User): Promise<any> {
        var httpBody = new HttpParams()
            .set("kilowantUViewed", (user.kilowantUViewed) ? "true" : "false")
            .set("firstAccountSetupFinished", (user.firstAccountSetupFinished) ? "true" : "false");
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer())
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return firstValueFrom(this.http
            .post<any>(
                environment.WEB_API_URL + "User/Onboarding/Init",
                httpBody,
                { headers: headers }
            ));
    }

    sendActivationLink(email: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("email", email);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/Activation",
                httpBody,
                { headers: headers }
            );
    }

    remindPassword(email: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("email", email);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/ForgotPasswordLink",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                return response;
            }
            ));
    }

    forgotPassword(data: string, password: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("data", data)
            .set("password", password);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/ForgotPasswordProceed",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                return response;
            }
            ));
    }

    forgotPasswordCheck(data: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("data", data);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/ForgotPasswordCheck",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                return response;
            }
            ));
    }

    loginActivateProceed(data: string, password: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("data", data)
            .set("password", password);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/ActivateLoginProceed",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                return response;
            }
            ));
    }

    loginActivateCheck(data: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("data", data);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/ActivateLoginCheck",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                return response;
            }
            ));
    }

    cleanUserRequest(): void {
        this._siteUserRequest = null;
    }

    getDetails(): Promise<User> {
        if (this._siteUserRequest != null) {
            return this._siteUserRequest;
        }
        var storedDetails = this.getFromSession("user_details");
        if (storedDetails != null) {
            return Promise.resolve(<User>storedDetails);
        }
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());

        this._siteUserRequest = <Promise<User>>this.http.get(environment.WEB_API_URL + "User/Details", { headers: headers })
            .toPromise()
            .catch((err: HttpErrorResponse) => {
                return null;
            })
            .then((res: any) => {
                var t = timer(1000);
                t.subscribe(t => {
                    this._siteUserRequest = null;
                });
                this.storeInSession("user_details", <User>res);
                return <User>res;
            });

        return this._siteUserRequest;
    }

    getAccountsExist(): Promise<boolean> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<boolean>>this.http.get(environment.WEB_API_URL + "Account/Any", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <boolean>res;
            });
    }

    getUserSavings(userFilter: Pair | null): Observable<TotalSavingsInfo> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        if (userFilter != null) {
            if (userFilter.key) {
                queryParams = queryParams.set("hasFilter", true);
                queryParams = queryParams.set("filterCompany", userFilter.value.id);
            } else {
                queryParams = queryParams.set("hasFilter", true);
            }
        }
        return this.http.get(environment.WEB_API_URL + "User/Savings", { headers: headers, params: queryParams })
            .pipe(
                map(response => {
                    return <TotalSavingsInfo>response;
                })
            );
    }

    getAccounts(activeOrInactive: boolean | null): Promise<Account[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("allAccounts", true);
        if (activeOrInactive != null) {
            queryParams = queryParams.set("activeOrInactive", (activeOrInactive) ? "true" : "false");
        }
        return <Promise<Account[]>>this.http.get(environment.WEB_API_URL + "Account/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((accounts: any) => {
                var res: Account[] = [];
                for (var i = 0; i < accounts.value.length; i++) {
                    if (accounts != null) {
                        res.push(Object.assign(new Account(), accounts.value[i].key));
                    }
                }
                return res;
            });
    }

    hasAITasks(): Promise<boolean> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<boolean>>this.http.get(environment.WEB_API_URL + "User/HasAITasks", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return res;
            });
    }

    getUsersActiveCompanies(): Observable<(Company | null)[]> {
        var storedCompanies = this.getFromSession("user_companies");
        if (storedCompanies != null) {
            return new Observable(observer => observer.next(<(Company | null)[]>storedCompanies));
        }
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("activeOnly", true);
        return this.http.get(environment.WEB_API_URL + "User/Companies", { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                this.storeInSession("user_companies", <(Company | null)[]>res);
                return <(Company | null)[]>res;
            }));
    }

    getUsersCompanies(): Observable<Company[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http.get(environment.WEB_API_URL + "User/Companies", { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                return <Company[]>res;
            }));
    }

    getCompaniesByAdvisor(client: User): Observable<Company[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        queryParams = queryParams.set("clientId", client.id);
        return this.http.get(environment.WEB_API_URL + "User/Companies", { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                return <Company[]>res;
            }));
    }

    clearUserCompaniesFromSession(): void {
        this.clearFromSessionByPrefix("user_companies");
        this.clearStoredUserFilter();
    }

    getAccountsPlusUsed(activeOrInactive: boolean | null, hasFilter: boolean, filterCompany: Company | null, keywords: string | null, total: number, page: number, numberRecords: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let res: Pair = null!;
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();

        if (keywords != null) {
            queryParams = queryParams.set("keywords", keywords);
        }
        if (activeOrInactive != null) {
            queryParams = queryParams.set("activeOrInactive", (activeOrInactive) ? "true" : "false");
        }
        if (hasFilter) {
            queryParams = queryParams.set("hasFilter", "true");
            if (filterCompany != null) {
                queryParams = queryParams.set("filterCompany", filterCompany.id);
            }
        }
        queryParams = queryParams.set("total", total)
            .set("page", page + 1)
            .set("numberRecords", numberRecords)
            .set("sortBy", sortBy)
            .set("isDesc", isDesc);

        queryParams = this.applyClientIfExistToGet(queryParams);
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Account/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((accounts: any) => {
                if (accounts != null) {
                    var accountList: Pair[] = [];
                    for (var i = 0; i < accounts.value.length; i++) {
                        accountList.push(new Pair(Object.assign(new Account(), accounts.value[i].key), accounts.value[i].value));
                    }
                    res = new Pair(accounts.key, accountList);
                }

                return res;
            });
    }

    getAccountsPlusCurrentCost(userFilter: Pair | null): Promise<AccountSelector[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (userFilter != null) {
            if (userFilter.key) {
                queryParams = queryParams.set("hasFilter", true);
                queryParams = queryParams.set("filterCompany", userFilter.value.id);
            } else {
                queryParams = queryParams.set("hasFilter", true);
            }
        }
        return <Promise<AccountSelector[]>>this.http.get(environment.WEB_API_URL + "Account/CurrentCost/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((accountsData: any) => {
                return accountsData;
            });
    }

    getAccountOldNumbers(account: Account): Promise<string[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<string[]>>this.http.get(environment.WEB_API_URL + "Account/OldAccountNumbers/" + account.id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return res;
            });
    }

    getAccountsNonUsedPerPeriod(sellOffers: SellOffer[] | null, basicAccount: Account | null, isMaxTheSavings: boolean | null | undefined, isBuyTheDip: boolean | null | undefined, isPlayItShort: boolean | null | undefined): Promise<AccountSelection[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (sellOffers != null) {
            const sellOfferIdsJSON = JSON.stringify(EntityBase.convertListToIDsArray(sellOffers));
            queryParams = queryParams.set("sellOfferIds", "" + sellOfferIdsJSON);
        }
        if (basicAccount != null) {
            queryParams = queryParams.set("accountId", "" + basicAccount.id);
        }
        if (isMaxTheSavings != null) {
            queryParams = queryParams.set("isMaxTheSavings", (isMaxTheSavings)?"true":"false");
        }
        if (isBuyTheDip != null) {
            queryParams = queryParams.set("isBuyTheDip", (isBuyTheDip) ? "true" : "false");
        }
        if (isPlayItShort != null) {
            queryParams = queryParams.set("isPlayItShort", (isPlayItShort) ? "true" : "false");
        }

        return <Promise<AccountSelection[]>>this.http.get(environment.WEB_API_URL + "Account/Unused/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                let outRes: AccountSelection[] = [];
                for (let i = 0; i < res.length; i++) {
                    if (res[i] != null) {
                        outRes.push(Object.assign(new AccountSelection(), res[i]));
                    }
                }
                return outRes;
            });
    }

    getAccountsNonUsedInAITask(): Promise<AccountSelection[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<AccountSelection[]>>this.http.get(environment.WEB_API_URL + "Account/Unused/AITasks/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                let outRes: AccountSelection[] = [];
                for (let i = 0; i < res.length; i++) {
                    if (res[i] != null) {
                        outRes.push(Object.assign(new AccountSelection(), res[i]));
                    }
                }
                return outRes;
            });
    }

    getAccountsForAITask(): Observable<AccountData[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http.get(environment.WEB_API_URL + "Account/AITasks/List", { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                let outRes: AccountData[] = [];
                for (let i = 0; i < res.length; i++) {
                    if (res[i] != null) {
                        outRes.push(Object.assign(new AccountData(), res[i]));
                    }
                }
                return outRes;
            }));
    }

    getAccountConsumptionYears(account: Account): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http.get(environment.WEB_API_URL + "Account/Consumption/Years/" + account.id, { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                return <Pair[]>res;
            }));
    }

    getAccountConsumptionType(account: Account): Observable<ConsumptionDataType> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return this.http.get(environment.WEB_API_URL + "Account/GetConsumptionType/" + account.id, { headers: headers, params: queryParams })
            .pipe(map((res: any) => {
                return <ConsumptionDataType>res;
            }));
    }

    getAccountConsumptions(account: Account, y: number): Promise<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Pair[]>>this.http.get(environment.WEB_API_URL + "Account/Consumption/Data/" + account.id + "/" + y, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                let existingConsumptions = <Pair[]>res;
                let consumptions: Pair[] = [];
                for (let i = 0; i <= 11; i++) {
                    let found = false;
                    for (let j = 0; j < existingConsumptions.length; j++) {
                        if (existingConsumptions[j].key.consumptionMonth == i + 1) {
                            consumptions.push(existingConsumptions[j]);
                            found = true;
                        }
                    }
                    if (!found) {
                        var c = new AccountConsumption();
                        c.consumptionYear = y;
                        c.consumptionMonth = i + 1;
                        c.consumption = null;
                        consumptions.push(new Pair(c, false));
                    }
                }
                return consumptions;
            });
    }

    addUpdateAccountConsumption(account: Account, accountConsumptions: AccountConsumption[]): Observable<any> {
        let accountConsumptionsJson = JSON.stringify(accountConsumptions);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("accountConsumptions", accountConsumptionsJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/Consumption/Data/Update/" + account.id,
                formData,
                { headers: headers, reportProgress: false }
            ).pipe(map(response => {
                return response;
            }
            )
            );
    }

    getAlerts(): Promise<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<Pair[]>>this.http.get(environment.WEB_API_URL + "User/Alerts", { headers: headers })
            .toPromise()
            .then((res: any) => {
                return <Pair[]>res;
            });
    }

    getSelectedAlerts(): Promise<AlertSubscription[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<AlertSubscription[]>>this.http.get(environment.WEB_API_URL + "User/SelectedAlerts", { headers: headers })
            .toPromise()
            .then((res: any) => {
                return <AlertSubscription[]>res;
            });
    }

    setSelectedAlerts(alerts: AlertSubscription[]): Observable<any> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        const formData: FormData = new FormData();
        const dataJson = JSON.stringify(alerts);
        formData.append("data", dataJson);

        return this.http
            .post<any>(
                environment.WEB_API_URL + "User/SelectAlerts",
                formData,
                { headers: headers }
            );
    }

    getSellerContract(id: number): Promise<SellerContract> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        return <Promise<SellerContract>>this.http.get(environment.WEB_API_URL + "SellerContract/Details/" + id, { headers: headers })
            .toPromise()
            .then((res: any) => {
                if (res != null) {
                    return <SellerContract>res;
                }
                return null;
            });
    }

    getSellerContracts(): Promise<SellerContract[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        return <Promise<SellerContract[]>>this.http.get(environment.WEB_API_URL + "SellerContracts", { headers: headers })
            .toPromise()
            .then((res: any) => {
                if (res != null) {
                    return <SellerContract[]>res;
                }
                return null;
            });
    }

    getUsedUtilitiesBySeller(): Promise<Utility[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        return <Promise<Utility[]>>this.http.get(environment.WEB_API_URL + "Seller/Utilities", { headers: headers })
            .toPromise()
            .then((res: any) => {
                if (res != null) {
                    return <Utility[]>res;
                }
                return null;
            });
    }

    addSellerContract(sellerContract: SellerContract, fileToUpload: File): Observable<any> {
        const sellerContractJson = JSON.stringify(sellerContract);
        const formData: FormData = new FormData();
        if (fileToUpload != null) {
            formData.append("termsDocument", fileToUpload, fileToUpload.name);
        }
        formData.append("sellerContract", sellerContractJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        var c = sellerContract;
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellerContract/Add",
                formData,
                { headers: headers }
            ).pipe(map(response => {
                if ((<number>response) > 0) {
                    c.id = (<number>response);
                }
                return response;
            }
            )
            );
    }

    updateSellerContract(sellerContract: SellerContract, fileToUpload: File, deleteTermsDocument: boolean): Observable<any> {
        const sellerContractJson = JSON.stringify(sellerContract);
        const formData: FormData = new FormData();
        if (fileToUpload != null) {
            formData.append("termsDocument", fileToUpload, fileToUpload.name);
        }
        formData.append("sellerContract", sellerContractJson);
        if (deleteTermsDocument) {
            formData.append("deleteTermsDocument", "true");
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellerContract/Update",
                formData,
                { headers: headers }
            );
    }

    copySellerContract(id: number): Observable<any> {
        var httpBody = new HttpParams()
            .set("id", id);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellerContract/Copy",
                httpBody,
                { headers: headers }
            );
    }

    deleteSellerContract(id: number): Observable<any> {
        var httpBody = new HttpParams()
            .set("id", id);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellerContract/Delete",
                httpBody,
                { headers: headers }
            );
    }

    getSellOfferList(total: number, page: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "SellOffer/List?sortby=" + sortBy + "&isdesc=" + isDesc + "&total=" + total + "&page=" + (page + 1), { headers: headers })
            .toPromise()
            .then((res: any) => {
                var dataPair = <Pair>res;
                var sellOffers: SellOffer[] = [];
                for (var i = 0; i < dataPair.value.length; i++) {
                    sellOffers.push(Object.assign(new SellOffer(), dataPair.value[i]))
                }
                this.changeHighlightedSource.next(true);
                return new Pair(dataPair.key, sellOffers);
            });
    }

    getOffersStartDatesListPerAccount(account: Account, fromDate: DateTime | null): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        if (fromDate != null) {
            queryParams = queryParams.set("periodFrom", fromDate.toISO()!.split('T')[0]);
        }
        return this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/" + account.id + "/StartDates", { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <Pair[]>data;
            })
            );
    }

    hasAdditionalOffersPerAccount(account: Account, periodFrom: Date | null, periodTo: Date | null,
        termFrom: number | null, termTo: number | null, savingsFrom: number | null,
        savingsTo: number | null, excludeSellOfferIds: number[] | null): Observable<boolean> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        if (excludeSellOfferIds != null && excludeSellOfferIds.length > 0) {
            const sellOfferIdsJSON = JSON.stringify(excludeSellOfferIds);
            queryParams = queryParams.set("excludeOfferIds", "" + sellOfferIdsJSON);
        }

        if (periodFrom != null) {
            queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        }
        if (periodTo != null) {
            queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        }
        if (termFrom != null) {
            queryParams = queryParams.set("termFrom", termFrom);
        }
        if (termTo != null) {
            queryParams = queryParams.set("termTo", termTo);
        }
        if (savingsFrom != null) {
            queryParams = queryParams.set("savingsFrom", savingsFrom);
        }
        if (savingsTo != null) {
            queryParams = queryParams.set("savingsTo", savingsTo);
        }
        return this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/" + account.id + "/Any", { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <boolean>data;
            })
            );
    }


    getOffersListPerAccount(account: Account, periodFrom: Date | null, periodTo: Date | null,
        termFrom: number | null, termTo: number | null, savingsFrom: number | null,
        savingsTo: number | null, excludeSellOfferIds: number[] | null,
        includeTotals: boolean|null,
        page: number, sortBy: string, isDesc: boolean): Observable<OffersResults> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        if (excludeSellOfferIds != null && excludeSellOfferIds.length > 0) {
            const sellOfferIdsJSON = JSON.stringify(excludeSellOfferIds);
            queryParams = queryParams.set("excludeOfferIds", "" + sellOfferIdsJSON);
        }

        if (periodFrom != null) {
            queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        }
        if (periodTo != null) {
            queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        }
        if (termFrom != null) {
            queryParams = queryParams.set("termFrom", termFrom);
        }
        if (termTo != null) {
            queryParams = queryParams.set("termTo", termTo);
        }
        if (savingsFrom != null) {
            queryParams = queryParams.set("savingsFrom", savingsFrom);
        }
        if (savingsTo != null) {
            queryParams = queryParams.set("savingsTo", savingsTo);
        }
        if (includeTotals != null) {
            queryParams = queryParams.set("includeTotals", (includeTotals)? "true" : "false");
        }
        queryParams = queryParams.set("page", page);
        queryParams = queryParams.set("sortBy", sortBy);
        queryParams = queryParams.set("isDesc", isDesc);
        return this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/" + account.id + "/List", { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                var records: OffersData[] = [];
                for (var i = 0; i < data.data.length; i++) {
                    let obj = new OffersData();
                    obj.offer = Object.assign(new SellOffer(), data.data[i].offer);
                    obj.savings = data.data[i].savings;
                    obj.priceStats = data.data[i].priceStats;
                    records.push(obj);
                }
                return new OffersResults(data.cnt, data.listOfTerms, data.listOfSavings, records);
            })
            );
    }

    getSellOfferPerAccount(account: Account | null, id: number): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (account != null) {
            queryParams = queryParams.set("accountId", account.id);
        }

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                var dataPair = <Pair>data;
                return new Pair(Object.assign(new SellOffer(), dataPair.key), dataPair.value);
            });
    }

    getSellOfferSellerContacts(id: number): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/ContractRoles/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                var dataPair = <Pair>data;
                return new Pair(Object.assign(new SellOffer(), dataPair.key), dataPair.value);
            });
    }


    getTopSavers(utility: Utility, loadZone: LoadZone | null, rateClass: RateClass): Promise<object[]> {
        let headers = new HttpHeaders();
        let queryParams = new HttpParams();
        queryParams = queryParams.set("utilityId", utility.id);
        if (loadZone != null) {
            queryParams = queryParams.set("loadZoneId", loadZone.id);
        }
        queryParams = queryParams.set("rateClassId", rateClass.id);

        return <Promise<object[]>>this.http.get(environment.WEB_API_URL + "Home/TopSavers", { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                return <object[]>data;
            });
    }

    getTopSellOfferPerAccount(account: Account, periodFrom: Date | null, periodTo: Date | null): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (periodFrom != null) {
            queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        }
        if (periodTo != null) {
            queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        }

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/" + account.id + "/TopOffers", { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                var dataList = <OffersData[]>data.value;
                var res = [];
                for (let i = 0; i < dataList.length; i++) {
                    var rec = Object.assign(new OffersData(), dataList[i]);
                    rec.offer = Object.assign(new SellOffer(), dataList[i].offer);
                    res.push(rec);
                }
                return new Pair(data.key, res);
            });
    }

    getPackagesPerAccount(account: Account): Observable<PackageData[] | null> {
        var pkgsFromCache = this.cacheService.getPackagesFromCache(account);
        if (pkgsFromCache != null) {
            return of<PackageData[] | null>(pkgsFromCache);
        }

        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return this.http
            .get<any>(
                environment.WEB_API_URL + "Buyer/SellOffer/" + account.id + "/Packages",
                { headers: headers, params: queryParams }
            )
            .pipe(
                map((data: any) => {
                    if (data == null || data.length === 0) {
                        return null;
                    }
                    const pkgs: PackageData[] = [];
                    for (let i = 0; i < data.length; i++) {
                        const pkg = Object.assign(new PackageData(), data[i]);
                        // Each entry is an OffersData, carrying its own inner SellOffer.
                        pkg.offers = (data[i].offers as any[]).map(raw => {
                            const rec = Object.assign(new OffersData(), raw);
                            if (raw.offer != null) {
                                rec.offer = Object.assign(new SellOffer(), raw.offer);
                            }
                            return rec;
                        });
                        pkgs.push(pkg);
                    }
                    this.cacheService.addPackagesToCache(account, pkgs);
                    return pkgs;
                }),
                catchError(() => of(null))
            );
    }

    clearPackagesPerAccount(account: Account): void {
        this.cacheService.clearPackagesFromCache(account);
    }

    getSellOffer(id: number): Promise<SellOffer> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<SellOffer>>this.http.get(environment.WEB_API_URL + "SellOffer/Details/" + id, { headers: headers })
            .toPromise()
            .then((res: any) => {
                return Object.assign(new SellOffer(), res);
            });
    }

    getPriceStatsPerSellOffer(sellOffer: SellOffer): Observable<Stats> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/PriceStats/" + sellOffer.id, { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <Stats>data;
            })
            );
    }

    getPriceHistoryPerSellOffer(sellOffer: SellOffer): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return this.http.get(environment.WEB_API_URL + "Buyer/SellOffer/PriceHistory/" + sellOffer.id, { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <Pair[]>data;
            })
            );
    }

    getPriceStatsPerContract(contract: KilowantContract): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return this.http.get(environment.WEB_API_URL + "Contract/PriceStats/" + contract.id, { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <Pair[]>data;
            })
            );
    }

    getPriceHistoryPerContract(contract: KilowantContract): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());

        return this.http.get(environment.WEB_API_URL + "Contract/PriceHistory/" + contract.id, { headers: headers, params: queryParams })
            .pipe(map((data: any) => {
                return <Pair[]>data;
            })
            );
    }

    addSellOffer(sellOffer: SellOffer): Observable<any> {
        const sellOfferJson = JSON.stringify(sellOffer);
        const formData: FormData = new FormData();
        formData.append("sellOffer", sellOfferJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        var b = sellOffer;
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellOffer/Add",
                formData,
                { headers: headers }
            ).pipe(map(response => {
                if ((<number>response) > 0) {
                    b.id = (<number>response);
                }
                return response;
            }
            )
            );
    }

    updateSellOffer(sellOffer: SellOffer): Observable<any> {
        const sellOfferJson = JSON.stringify(sellOffer);
        const formData: FormData = new FormData();
        formData.append("sellOffer", sellOfferJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellOffer/Update",
                formData,
                { headers: headers }
            );
    }

    deleteSellOffer(id: number): Observable<any> {
        var httpBody = new HttpParams()
            .set("id", id);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellOffer/Delete",
                httpBody,
                { headers: headers }
            );
    }

    markSellOfferViewed(sellOffer: SellOffer): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("sellOfferId", "" + sellOffer.id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SellOffer/Viewed",
                formData,
                { headers: headers }
            );
    }

    acceptSellOffer(sellOffer: SellOffer, accountIds: number[], aiSubscription: boolean): Observable<number> {
        let accountIdsJSON = JSON.stringify(accountIds);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("accountIds", accountIdsJSON);
        formData.append("aiSubscription", (aiSubscription) ? "true" : "false");
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<number>(
                environment.WEB_API_URL + "Buyer/SellOffer/Accept/" + sellOffer.id,
                formData,
                { headers: headers }
            ).pipe(map(response => {
                this.newContractCreatedSource.next(<number>response);
                return response;
            }
            )
            );
    }

    acceptPackage(pkg: PackageData, accountIds: number[], aiSubscription: boolean): Observable<number> {
        let accountIdsJSON = JSON.stringify(accountIds);
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("accountIds", accountIdsJSON);
        formData.append("aiSubscription", (aiSubscription) ? "true" : "false");
        const sellOfferIdsJSON = JSON.stringify(EntityBase.convertListToIDsArray(pkg.offers.map(x => x.offer)));
        formData.append("sellOfferIds", "" + sellOfferIdsJSON);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<number>(
                environment.WEB_API_URL + "Buyer/Package/Accept",
                formData,
                { headers: headers }
            )
            .pipe(map(response => {
                this.newContractCreatedSource.next(<number>response);
                return <number>response;
            }));
    }


    markBuyOfferViewed(buyOffer: BuyOffer): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("buyOfferId", "" + buyOffer.id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/BuyOffer/Viewed",
                formData,
                { headers: headers }
            );
    }

    acceptBuyOffer(buyOffer: BuyOffer, sellerContract: SellerContract): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("sellerContractId", "" + sellerContract.id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/BuyOffer/Accept/" + buyOffer.id,
                formData,
                { headers: headers }
            );
    }

    markContractViewed(contract: KilowantContract): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("contractId", "" + contract.id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/Contract/Viewed",
                formData,
                { headers: headers }
            );
    }

    confirmContract(contract: KilowantContract, termsConditions: SellerContract | null): Observable<any> {
        const formData: FormData = new FormData();
        const contractJson = JSON.stringify(contract);
        formData.append("contract", contractJson);
        if (termsConditions != null) {
            formData.append("sellerContractId", "" + termsConditions.id);
        }
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/Contract/Confirm/" + contract.id,
                formData,
                { headers: headers }
            );
    }

    declineContract(contract: KilowantContract, declineReasons: string): Observable<any> {
        const formData: FormData = new FormData();
        const contractJson = JSON.stringify(contract);
        formData.append("declineReasons", declineReasons);
        formData.append("contract", contractJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Seller/Contract/Decline/" + contract.id,
                formData,
                { headers: headers }
            );
    }

    getAccount(id: number): Promise<Account> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Account>>this.http.get(environment.WEB_API_URL + "Account/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Account>res;
            });
    }

    getAccountCurrentCosts(id: number): Promise<CurrentContractData[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<CurrentContractData[]>>this.http.get(environment.WEB_API_URL + "Account/CurrentCost/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var currentContractData: CurrentContractData[] = [];
                for (var i = 0; i < res.length; i++) {
                    let cData = Object.assign(new CurrentContractData(), res[i]);
                    if (res[i].termFrom != null) {
                        cData.termFrom = new Date(Date.parse(res[i].termFrom));
                    }
                    if (res[i].rateEnd != null) {
                        cData.rateEnd = new Date(Date.parse(res[i].rateEnd));
                    }
                    if (res[i].termTo != null) {
                        cData.termTo = new Date(Date.parse(res[i].termTo));
                    }
                    currentContractData.push(cData);
                }
                return currentContractData;
            });
    }

    getAccountPrevYearCost(id: number): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Account/PrevYearCost/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Pair>res;
            });
    }

    getAccountGetLatestUtilityDate(id: number): Promise<Pair> {
        var cacheName = "account_data_latestUtilityDate_" + id;
        let cache = this.getFromSession(cacheName);
        if (cache != null) {
            return Promise.resolve(<Pair>cache);
        }
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Account/GetLatestUtilityDate/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                this.storeInSession(cacheName, <Pair>res);
                return <Pair>res;
            });
    }

    getBuyOfferProposedPrice(account: Account): Promise<number> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("accountId", account.id);

        return <Promise<number>>this.http.get(environment.WEB_API_URL + "Buyer/BuyOffer/Generated", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <number>res;
            });
    }

    deleteAccount(id: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("id", "" + id);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "Account/Delete",
                formData,
                { headers: headers }
            );
    }

    deleteExistingContract(id: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("id", "" + id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "ExistingContract/Delete",
                formData,
                { headers: headers }
            );
    }

    getBuyOffers(isOpen: boolean): Promise<BuyOffer[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("isClosed", (isOpen) ? "false" : "true");
        return <Promise<BuyOffer[]>>this.http.get(environment.WEB_API_URL + "BuyOffer/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var buyOffers: BuyOffer[] = [];
                for (var i = 0; i < res.length; i++) {
                    buyOffers.push(Object.assign(new BuyOffer(), res[i]));
                }
                return buyOffers;
            });
    }

    getBuyOffersForSeller(total: number, page: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        queryParams = queryParams.set("total", total);
        queryParams = queryParams.set("page", page + 1);
        queryParams = queryParams.set("sortBy", sortBy);
        queryParams = queryParams.set("isDesc", isDesc);

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Seller/BuyOffer/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var dataPair = <Pair>res;
                var buyOffers: BuyOffer[] = [];
                for (var i = 0; i < dataPair.value.length; i++) {
                    buyOffers.push(Object.assign(new BuyOffer(), dataPair.value[i]))
                }
                this.changeHighlightedSource.next(true);
                return new Pair(dataPair.key, buyOffers);
            });
    }

    getBuyOffer(id: number): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "BuyOffer/Details/" + id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var dataPair = <Pair>res;
                return new Pair(Object.assign(new BuyOffer(), dataPair.key), dataPair.value);
            });
    }

    getBuyOffersPerAccount(account: Account, isClosed: boolean | null, periodFrom: Date | null, periodTo: Date | null,
        total: number, page: number, numberRecords: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (isClosed != null) {
            queryParams = queryParams.set("isClosed", isClosed);
        }
        if (periodFrom != null) {
            queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        }
        if (periodTo != null) {
            queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        }
        queryParams = queryParams.set("total", total);
        queryParams = queryParams.set("page", page + 1);
        queryParams = queryParams.set("numberRecords", numberRecords);
        queryParams = queryParams.set("sortBy", sortBy);
        queryParams = queryParams.set("isDesc", isDesc);

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "BuyOffer/" + account.id + "/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Pair>res;
            });
    }

    getBids(buyOffer: BuyOffer): Promise<Bid[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        return <Promise<Bid[]>>this.http.get(environment.WEB_API_URL + "BuyOffer/Details/" + buyOffer.id + "/Bids", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Bid[]>res;
            });
    }

    deleteBuyOffer(id: number): Observable<any> {
        let formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("id", "" + id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/Delete",
                formData,
                { headers: headers }
            );
    }

    addBuyOffer(accounts: Account[], buyOffer: BuyOffer): Observable<any> {
        const buyOfferJson = JSON.stringify(buyOffer);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);

        var accountIds = [];

        for (var i = 0; i < accounts.length; i++) {
            accountIds[accountIds.length] = accounts[i].id;
        }
        formData.append("accountIds", JSON.stringify(accountIds));
        formData.append("buyOffer", buyOfferJson);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/Add",
                formData,
                { headers: headers }
            );
    }

    updateBuyOffer(buyOffer: BuyOffer): Observable<any> {
        const buyOfferJson = JSON.stringify(buyOffer);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("buyOffer", buyOfferJson);

        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/Update",
                formData,
                { headers: headers }
            );
    }

    updateBid(buyOffer: BuyOffer, bid: Bid): Observable<any> {
        const bidJson = JSON.stringify(bid);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("bid", bidJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/Details/" + buyOffer.id + "/UpdateBid",
                formData,
                { headers: headers }
            );
    }

    addBid(buyOffer: BuyOffer, bid: Bid): Observable<any> {
        const bidJson = JSON.stringify(bid);
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("bid", bidJson);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/Details/" + buyOffer.id + "/UpdateBid",
                formData,
                { headers: headers }
            );
    }

    deleteBid(id: number): Observable<any> {
        const formData: FormData = new FormData();
        this.applyClientIfExistToPost(formData);
        formData.append("id", "" + id);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BuyOffer/DeleteBid",
                formData,
                { headers: headers }
            );
    }

    getClients(isResidential: boolean | null, activeOrInactive: boolean | null, keywords: string | null, total: number, page: number, numberRecords: number, sortBy: string, isDesc: boolean): Promise<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        if (isResidential != null) {
            if (isResidential) {
                queryParams = queryParams.set("isResidential", "true");
            } else {
                queryParams = queryParams.set("isResidential", "false");
            }
        }
        if (activeOrInactive != null) {
            queryParams = queryParams.set("activeOrInactive", (activeOrInactive) ? "true" : "false");
        }
        if (keywords != null) {
            queryParams = queryParams.set("keywords", keywords);
        }
        queryParams = queryParams.set("total", total)
            .set("page", page + 1)
            .set("numberRecords", numberRecords)
            .set("sortBy", sortBy)
            .set("isDesc", isDesc);

        return <Promise<Pair>>this.http.get(environment.WEB_API_URL + "Advisor/Buyers/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <Pair>res;
            });
    }

    getAdvisorReportUsersAccounts(): Observable<any> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .get(
                environment.WEB_API_URL + "Advisor/ReportUsersAccounts",
                {
                    headers: headers,
                    responseType: "arraybuffer"
                }
            );
    }

    quickSearchClients(showResidential: boolean, showCommercial: boolean, query: string): Promise<ClientFilter[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams()
            .set("search", query);
        if (showResidential) {
            queryParams = queryParams.set("showResidential", "true");
        }
        if (showCommercial) {
            queryParams = queryParams.set("showCommercial", "true");
        }
        return <Promise<ClientFilter[]>>this.http.get(environment.WEB_API_URL + "Advisor/Buyers/Search", { headers: headers, params: queryParams })
            .toPromise()
            .then((users: any) => {
                return <ClientFilter[]>users;
            });
    }

    getClient(id: number): Observable<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        return this.http.get(environment.WEB_API_URL + "Advisor/Buyers/" + id, { headers: headers, params: queryParams })
            .pipe(
                map((res: any) => {
                    return new Pair(Object.assign(new User(), res.key), (res.value == null) ? null : Object.assign(new User(), res.value));
                })
            );
    }

    getContactManagers(): Observable<Pair[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return this.http.get(environment.WEB_API_URL + "Advisor/Buyers/ManagerContacts", { headers: headers })
            .pipe(
                map((res: any) => {
                    return <Pair[]>res;
                })
            );
    }

    getClientByContractId(contractId: number): Promise<User> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = new HttpParams();
        return <Promise<User>>this.http.get(environment.WEB_API_URL + "Advisor/Buyers/ByContract/" + contractId, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return Object.assign(new User(), res);
            });
    }

    getFile(buyOffer: BuyOffer, document: Pair): Observable<any> {
        var httpBody = this.applyClientIfExistToGet(new HttpParams())
            .set("buyOfferId", buyOffer.id)
            .set("documentId", document.key);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post(
                environment.WEB_API_URL + "BuyOffer/SendFile",
                httpBody,
                {
                    headers: headers,
                    responseType: "arraybuffer"
                }
            );
    }

    getBulkImportTemplate(name: string): Observable<any> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .get(
                environment.WEB_API_URL + "BulkImport/GetTemplateCSV?name=" + name,
                {
                    headers: headers,
                    responseType: "arraybuffer"
                }
            );
    }

    bulkImportProceed(fileToUpload: File): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("importDocument", fileToUpload, fileToUpload.name);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "BulkImport/Proceed",
                formData,
                { headers: headers, reportProgress: true, observe: "events" }
            );
    }

    getBatchExport(batch: string): Observable<any> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .get(
                environment.WEB_API_URL + "BatchExport/Download?batch=" + batch,
                {
                    headers: headers,
                    responseType: "arraybuffer"
                }
            );
    }

    getBatchExportList(): Promise<BatchExport[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<BatchExport[]>>this.http.get(environment.WEB_API_URL + "BatchExport/List", { headers: headers })
            .toPromise()
            .then((data: any) => {
                var res: BatchExport[] = [];
                for (var i = 0; i < data.length; i++) {
                    res.push(Object.assign(new BatchExport(), data[i]));
                }
                return res;
            });
    }

    getFTPData(): Observable<Pair | null> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());

        let queryParams = new HttpParams();

        return this.http.get(environment.WEB_API_URL + "Seller/FTPData", { headers: headers, params: queryParams })
            .pipe(
                map((res: any) => {
                    return <Pair>res;
                })
            );
    }

    getSellersList(total: number, page: number, sortBy: string, isDesc: boolean, activeOnly: boolean = true): Observable<Pair> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return this.http.get(environment.WEB_API_URL + "Seller/List?sortby=" + sortBy + "&isdesc=" + isDesc + "&total=" + total + "&page=" + (page + 1) + "&activeonly=" + activeOnly, { headers: headers })
            .pipe(
                map((res: any) => {
                    return <Pair>res;
                })
            );
    }

    getSellersListForAssociation(): Promise<Company> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<Company>>this.http.get(environment.WEB_API_URL + "Seller/Associate", { headers: headers })
            .toPromise()
            .then((res: any) => {
                return <Company>res;
            });
    }

    getContractRateListPerAccount(account: Account, periodFrom: Date, periodTo: Date): Promise<ChartData[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        return <Promise<ChartData[]>>this.http.get(environment.WEB_API_URL + "Chart/Home/Rates/" + account.id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                var chartData: ChartData[] = [];
                for (var i = 0; i < res.length; i++) {
                    chartData.push(Object.assign(new ChartData(), res[i]));
                }
                return chartData;
            });
    }

    getHistoricalRatesPerAccount(account: Account, periodFrom: Date | null, periodTo: Date | null, autoAdjustZoom: boolean, useQuartalPeriod: boolean, noKilowantPrice: boolean, showUtility: boolean, showCommunity: boolean): Promise<RateData[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        if (periodFrom != null) {
            queryParams = queryParams.set("periodFrom", this.datepipe.transform(periodFrom, "MM/dd/yyyy") ?? "");
        }
        if (periodTo != null) {
            queryParams = queryParams.set("periodTo", this.datepipe.transform(periodTo, "MM/dd/yyyy") ?? "");
        }
        queryParams = queryParams.set("autoAdjustZoom", autoAdjustZoom);
        queryParams = queryParams.set("noKilowantPrice", noKilowantPrice);
        queryParams = queryParams.set("useQuartalPeriod", useQuartalPeriod);

        queryParams = queryParams.set("showUtility", showUtility);
        queryParams = queryParams.set("showCommunity", showCommunity);

        return <Promise<RateData[]>>this.http.get(environment.WEB_API_URL + "Chart/Home/Rates/History/" + account.id, { headers: headers, params: queryParams })
            .toPromise()
            .then((res: any) => {
                return <RateData[]>res;
            });
    }

    getFutureContractsPerAccount(account: Account): Promise<Contract[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        let queryParams = this.applyClientIfExistToGet(new HttpParams());
        queryParams = queryParams.set("accountId", account.id);

        return <Promise<Contract[]>>this.http.get(environment.WEB_API_URL + "Home/Future/Contracts/List", { headers: headers, params: queryParams })
            .toPromise()
            .then((data: any) => {
                var res: Contract[] = [];
                for (var i = 0; i < data.length; i++) {
                    if (data[i].value) {
                        res.push(Object.assign(new KilowantContract(), data[i].key));
                    } else {
                        res.push(Object.assign(new ExistingContract(), data[i].key));
                    }
                }
                return res;
            });
    }

    getKilowantFeesList(): Promise<KilowantFee[]> {
        let headers = new HttpHeaders()
            .set("Authorization", this.authService.getBearer());
        return <Promise<KilowantFee[]>>this.http.get(environment.WEB_API_URL + "KilowantFee/List", { headers: headers })
            .toPromise()
            .then((res: any) => {
                return <KilowantFee[]>res;
            });
    }

    sendMessage(message: string): Observable<any> {
        const formData: FormData = new FormData();
        formData.append("data", message);
        this.applyClientIfExistToPost(formData);
        let headers = new HttpHeaders()
            .set("Accept", "application/json")
            .set("Authorization", this.authService.getBearer());
        return this.http
            .post<any>(
                environment.WEB_API_URL + "SendMessage",
                formData,
                { headers: headers }
            );
    }

    applyClientIfExistToGet(queryParams: HttpParams): HttpParams {
        var client = this.getStoredClient();
        if (client != null && client.id != null) {
            queryParams = queryParams.set("clientId", client.id);
        }
        return queryParams;
    }

    applyClientIfExistToPost(formData: FormData): void {
        var client = this.getStoredClient();
        if (client != null && client.id != null) {
            formData.append("clientId", "" + client.id);
        }
    }

    getPairValueByKey(key: string, data: Pair[]): string {
        for (var i = 0; i < data.length; i++) {
            if (data[i].key == key) {
                return data[i].value;
            }
        }
        return null!;
    }

    isPositiveInteger(input: string): boolean {
        var num = Number(input);
        if (isNaN(num)) {
            return false;
        }
        if (num <= 0) {
            return false;
        }
        if (num - Math.round(num) > 0) {
            return false;
        }
        return true;
    }

    sendFileToClient(file: File | null): void {
        if (file == null) {
            return;
        }
        const fileUrl = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.dispatchEvent(
            new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window
            })
        );
    }

    getDocumentUrl(id: number): Observable<string | null> {
        let headers = new HttpHeaders().set("Authorization", this.authService.getBearer());
        var client = this.getStoredClient();
        let params = new HttpParams().set("documentId", id + "");
        if (client != null && client.id != null) {
            params = params.set("clientId", client.id);
        }
        return this.http.get(
            environment.WEB_API_URL + "Document/Url",
            { headers, params, responseType: 'text' }
        ).pipe(map(url => (url && url !== 'null') ? url : null));
    }

    openDocument(id: number): void {
        this.getDocumentUrl(id).subscribe(url => {
            if (url) {
                window.open(url, '_blank');
            } else {
                this.sendFileFromServerToClient(id);
            }
        });
    }

    sendFileFromServerToClient(id: number): void {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", this.authService.getBearer());
        var client = this.getStoredClient();
        var httpBody = new HttpParams()
            .set("documentId", id + "");

        if (client != null && client.id != null) {
            httpBody = httpBody.set("clientId", client.id);
        }

        this.http.post(
            environment.WEB_API_URL + "User/SendFile",
            httpBody,
            {
                headers: headers,
                responseType: "blob" as "json",
                observe: "response"
            }
        ).subscribe((response: any) => {
            var filename = "";
            var disposition = response.headers.get("Content-Disposition");
            if (disposition && disposition.indexOf("attachment") !== -1) {
                var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                var matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, "");
            }
            const link = document.createElement("a");
            link.href = URL.createObjectURL(response.body);
            link.download = filename;
            link.dispatchEvent(
                new MouseEvent("click", {
                    bubbles: true,
                    cancelable: true,
                    view: window
                })
            );
        });
    }

    public getRateClassAbbr(rateClass: string): string {
        var matches = rateClass.match(/\(([.+?]+?)\)/);
        if (matches != null && matches[1] != null && matches[1].length > 0) {
            return matches[1];
        }
        var matches = rateClass.match(/([\S+]+?\s)/);
        if (matches != null && matches[1] != null && matches[1].length > 0) {
            return matches[1];
        }
        return rateClass;
    }

    public setPageContent(pageContent: MatDrawerContent) {
        this._pageContent = pageContent;
    }

    public setHeaderHeight(headerHeight: number): void {
        this._headerHeight = headerHeight;
    }

    public getHeaderHeight(): number {
        return this._headerHeight;
    }

    public scrollPageBottom(scrollBottom: number) {
        if (this._pageContent != null) {
            this._pageContent.scrollTo({ bottom: scrollBottom, behavior: "smooth" });
        }
    }

    public scrollPageTop(scrollTop: number) {
        if (this._pageContent != null) {
            this._pageContent.scrollTo({ top: scrollTop, behavior: "smooth" });
        }
    }

    public scrollPageBy(scrollBy: number) {
        if (this._pageContent != null) {
            let currentScrolledValue = this._pageContent.measureScrollOffset("top");
            this._pageContent.scrollTo({ top: (currentScrolledValue + scrollBy), behavior: "smooth" });

        }
    }

    public setSidenav(sidenav: MatSidenav) {
        this._sidenav = sidenav;
    }

    public openSidenav() {
        return this._sidenav.open();
    }

    public closeSidenav() {
        return this._sidenav.close();
    }

    public toggleSidenav(): void {
        this._sidenav.toggle();
    }

    public stringOrNull(str: string | null) {
        return (str != null) ? str : "";
    }

}
